/**
 * mSTEM New Project Modal (PRJ-01 / LLD UC 06). Presentational component
 * — Redux wiring lives in src/containers/new-project-modal.jsx.
 *
 * 3 form fields per LLD UC 06:
 *   1. Tên dự án (required, validate non-empty + unique within
 *      mstem.myProjects[])
 *   2. Loại thiết bị kết nối (dropdown — Robot mSTEM / Kit SmartHome /
 *      Không kết nối thiết bị)
 *   3. Mô tả dự án (optional textarea)
 *
 * 2 buttons:
 *   - Hủy bỏ (secondary)
 *   - Xác nhận tạo (primary navy)
 *
 * Validation strings live in this file alongside the input render so the
 * Phase 3 aggregated i18n test (test/unit/i18n/phase-3-messages.test.js)
 * can pick them up by id during extraction.
 */
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import {defineMessages, FormattedMessage, injectIntl, intlShape} from 'react-intl';

import Modal from '../../containers/modal.jsx';

import styles from './new-project-modal.css';

const messages = defineMessages({
    modalTitle: {
        defaultMessage: 'Tạo dự án mới',
        description: 'Title of New Project modal (PRJ-01, LLD UC 06)',
        id: 'gen.project.new.title'
    },
    fieldName: {
        defaultMessage: 'Tên dự án',
        description: 'Project name field label',
        id: 'gen.project.field.name'
    },
    fieldType: {
        defaultMessage: 'Loại thiết bị kết nối',
        description: 'Device type field label (Robot/Kit/None)',
        id: 'gen.project.field.type'
    },
    fieldDescription: {
        defaultMessage: 'Mô tả dự án',
        description: 'Project description field label (optional)',
        id: 'gen.project.field.description'
    },
    typeRobot: {
        defaultMessage: 'Robot mSTEM',
        description: 'Device type option: Robot mSTEM',
        id: 'gen.project.type.robot'
    },
    typeKit: {
        defaultMessage: 'Kit SmartHome',
        description: 'Device type option: Kit SmartHome',
        id: 'gen.project.type.kit'
    },
    typeNone: {
        defaultMessage: 'Không kết nối thiết bị',
        description: 'Device type option: no device',
        id: 'gen.project.type.none'
    },
    typePlaceholder: {
        defaultMessage: 'Chọn loại thiết bị...',
        description: 'Device type dropdown placeholder',
        id: 'gen.project.type.placeholder'
    },
    descPlaceholder: {
        defaultMessage: 'Mô tả ngắn về dự án của bạn (tùy chọn)',
        description: 'Description textarea placeholder',
        id: 'gen.project.description.placeholder'
    },
    cancel: {
        defaultMessage: 'Hủy bỏ',
        description: 'Cancel button label (universal)',
        id: 'gen.project.action.cancel'
    },
    confirmCreate: {
        defaultMessage: 'Xác nhận tạo',
        description: 'Confirm create button label (PRJ-01)',
        id: 'gen.project.new.confirm'
    },
    errNameRequired: {
        defaultMessage: 'Vui lòng nhập tên dự án',
        description: 'Validation: name is required',
        id: 'gen.project.error.nameRequired'
    },
    errNameDuplicate: {
        defaultMessage: 'Đã có dự án trùng tên. Vui lòng chọn tên khác',
        description: 'Validation: name already exists',
        id: 'gen.project.error.nameDuplicate'
    },
    errNameTooLong: {
        defaultMessage: 'Tên dự án tối đa 100 ký tự',
        description: 'Validation: name exceeds 100 chars',
        id: 'gen.project.error.nameTooLong'
    },
    errTypeRequired: {
        defaultMessage: 'Vui lòng chọn loại thiết bị',
        description: 'Validation: device type required',
        id: 'gen.project.error.typeRequired'
    },
    errDescTooLong: {
        defaultMessage: 'Mô tả tối đa 2000 ký tự',
        description: 'Validation: description exceeds 2000 chars',
        id: 'gen.project.error.descTooLong'
    }
});

const NewProjectModalComponent = ({
    name, type, description, errors, intl,
    onChangeName, onChangeType, onChangeDescription,
    onBlurName, onBlurType, onBlurDescription,
    onSubmit, onCancel
}) => (
    <Modal
        className={styles.modalContent}
        contentLabel={intl.formatMessage(messages.modalTitle)}
        onRequestClose={onCancel}
    >
        <div className={styles.body}>
            <form
                className={styles.editForm}
                noValidate
                onSubmit={onSubmit}
            >
                <div className={styles.formField}>
                    <label
                        className={styles.formLabel}
                        htmlFor="np-name"
                    >
                        <FormattedMessage {...messages.fieldName} />
                        <span className={styles.required}>{'*'}</span>
                    </label>
                    <input
                        autoFocus
                        aria-describedby={errors.name ? 'np-name-error' : null}
                        aria-invalid={Boolean(errors.name)}
                        className={classNames(styles.formInput, {
                            [styles.formInputError]: errors.name
                        })}
                        id="np-name"
                        maxLength={100}
                        type="text"
                        value={name}
                        onBlur={onBlurName}
                        onChange={onChangeName}
                    />
                    {errors.name ? (
                        <span
                            className={styles.formError}
                            id="np-name-error"
                        >
                            <FormattedMessage {...messages[errors.name]} />
                        </span>
                    ) : null}
                </div>

                <div className={styles.formField}>
                    <label
                        className={styles.formLabel}
                        htmlFor="np-type"
                    >
                        <FormattedMessage {...messages.fieldType} />
                        <span className={styles.required}>{'*'}</span>
                    </label>
                    <select
                        aria-describedby={errors.type ? 'np-type-error' : null}
                        aria-invalid={Boolean(errors.type)}
                        className={classNames(styles.formInput, {
                            [styles.formInputError]: errors.type
                        })}
                        id="np-type"
                        value={type}
                        onBlur={onBlurType}
                        onChange={onChangeType}
                    >
                        <option value="">{intl.formatMessage(messages.typePlaceholder)}</option>
                        <option value="robot">{intl.formatMessage(messages.typeRobot)}</option>
                        <option value="kit">{intl.formatMessage(messages.typeKit)}</option>
                        <option value="none">{intl.formatMessage(messages.typeNone)}</option>
                    </select>
                    {errors.type ? (
                        <span
                            className={styles.formError}
                            id="np-type-error"
                        >
                            <FormattedMessage {...messages[errors.type]} />
                        </span>
                    ) : null}
                </div>

                <div className={styles.formField}>
                    <label
                        className={styles.formLabel}
                        htmlFor="np-desc"
                    >
                        <FormattedMessage {...messages.fieldDescription} />
                    </label>
                    <textarea
                        aria-describedby={errors.description ? 'np-desc-error' : null}
                        aria-invalid={Boolean(errors.description)}
                        className={classNames(
                            styles.formInput,
                            styles.formTextarea,
                            {[styles.formInputError]: errors.description}
                        )}
                        id="np-desc"
                        maxLength={2000}
                        placeholder={intl.formatMessage(messages.descPlaceholder)}
                        rows={4}
                        value={description}
                        onBlur={onBlurDescription}
                        onChange={onChangeDescription}
                    />
                    {errors.description ? (
                        <span
                            className={styles.formError}
                            id="np-desc-error"
                        >
                            <FormattedMessage {...messages[errors.description]} />
                        </span>
                    ) : null}
                </div>

                <div className={styles.formActions}>
                    <button
                        className={styles.btnSecondary}
                        type="button"
                        onClick={onCancel}
                    >
                        <FormattedMessage {...messages.cancel} />
                    </button>
                    <button
                        className={styles.btnPrimary}
                        type="submit"
                    >
                        <FormattedMessage {...messages.confirmCreate} />
                    </button>
                </div>
            </form>
        </div>
    </Modal>
);

NewProjectModalComponent.propTypes = {
    description: PropTypes.string.isRequired,
    errors: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
    intl: intlShape.isRequired,
    name: PropTypes.string.isRequired,
    onBlurDescription: PropTypes.func.isRequired,
    onBlurName: PropTypes.func.isRequired,
    onBlurType: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    onChangeDescription: PropTypes.func.isRequired,
    onChangeName: PropTypes.func.isRequired,
    onChangeType: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    type: PropTypes.string.isRequired
};

export default injectIntl(NewProjectModalComponent);
